import { useState, useEffect, useMemo } from 'react';
import type { Desafio } from '../../types/quiz.types';
import { shuffleArray } from '../../utils/quizUtils';

interface Props {
  desafio: Desafio;
  onComplete: (sucesso: boolean) => void;
}

/**
 * Ver as DUAS colunas ao mesmo tempo é a mecânica deste desafio — rolagem é
 * proibida (L1) e, sem válvula, conteúdo que não coubesse seria cortado pelo
 * overflow-hidden do shell. Pior caso do dataset: 4 pares com termos de até
 * 98 caracteres, que estoura o palco de 360×640. Solução: fatiamento
 * automático em rodadas de 2 pares SÓ quando a projeção de altura estoura;
 * desafios típicos seguem em rodada única. A regra de acerto não muda:
 * onComplete(sucesso) continua único, ao final de todas as conexões.
 */

// Projeção do pior caso em 360×640: coluna ~140px a 11px ≈ 22 chars/linha;
// botão = max(44px de alvo L7, linhas × 15px + 12px de padding).
const CHARS_POR_LINHA = 22;
const PALCO_DISPONIVEL_360 = 340;
const alturaBotaoProjetada = (chars: number) =>
  Math.max(44, Math.ceil(Math.max(1, chars) / CHARS_POR_LINHA) * 15 + 12);

export function ConexaoSaberesMinigame({ desafio, onComplete }: Props) {
  const recompensa = desafio.recompensa?.moedas ?? 0;
  const pares = desafio.pares ?? [];

  // Fatia os índices em rodadas de 2 quando a coluna projetada não cabe.
  const rodadas = useMemo(() => {
    const todos = pares.map((_, i) => i);
    if (todos.length === 0) return [todos];
    const alturaColuna = (lado: 'esq' | 'dir') =>
      todos.reduce((s, i) => s + alturaBotaoProjetada((lado === 'esq' ? pares[i].termo_esq : pares[i].termo_dir).length), 0)
      + (todos.length - 1) * 6 + 24;
    const estoura = Math.max(alturaColuna('esq'), alturaColuna('dir')) > PALCO_DISPONIVEL_360;
    if (!estoura || todos.length <= 2) return [todos];
    const chunks: number[][] = [];
    for (let i = 0; i < todos.length; i += 2) chunks.push(todos.slice(i, i + 2));
    return chunks;
  }, [pares]);

  const [rodadaAtual, setRodadaAtual] = useState(0);
  const fatiado = rodadas.length > 1;
  const idsDaRodada = rodadas[Math.min(rodadaAtual, rodadas.length - 1)] ?? [];

  const [termosEsq, setTermosEsq] = useState<{ termo: string; idOriginal: number }[]>([]);
  const [termosDir, setTermosDir] = useState<{ termo: string; idOriginal: number }[]>([]);

  const [selecionadoEsq, setSelecionadoEsq] = useState<number | null>(null);
  const [selecionadoDir, setSelecionadoDir] = useState<number | null>(null);
  const [conexoes, setConexoes] = useState<{ esqId: number; dirId: number }[]>([]);

  const coresPares = [
    'border-emerald-500 bg-emerald-500/20 text-emerald-300',
    'border-blue-500 bg-blue-500/20 text-blue-300',
    'border-purple-500 bg-purple-500/20 text-purple-300',
    'border-pink-500 bg-pink-500/20 text-pink-300',
    'border-amber-500 bg-amber-500/20 text-amber-300',
  ];

  useEffect(() => {
    if (pares.length > 0) {
      const esquerdos = idsDaRodada.map((idOriginal) => ({ termo: pares[idOriginal].termo_esq, idOriginal }));
      const direitos = idsDaRodada.map((idOriginal) => ({ termo: pares[idOriginal].termo_dir, idOriginal }));
      setTermosEsq(shuffleArray(esquerdos));
      setTermosDir(shuffleArray(direitos));
      setSelecionadoEsq(null);
      setSelecionadoDir(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pares, rodadaAtual]);

  const handleEsqClick = (id: number) => {
    const connIndex = conexoes.findIndex(c => c.esqId === id);
    if (connIndex !== -1) {
      const novasConexoes = [...conexoes];
      novasConexoes.splice(connIndex, 1);
      setConexoes(novasConexoes);
      setSelecionadoEsq(null);
      return;
    }

    if (selecionadoEsq === id) {
      setSelecionadoEsq(null);
    } else {
      setSelecionadoEsq(id);
      if (selecionadoDir !== null) {
        setConexoes([...conexoes, { esqId: id, dirId: selecionadoDir }]);
        setSelecionadoEsq(null);
        setSelecionadoDir(null);
      }
    }
  };

  const handleDirClick = (id: number) => {
    const connIndex = conexoes.findIndex(c => c.dirId === id);
    if (connIndex !== -1) {
      const novasConexoes = [...conexoes];
      novasConexoes.splice(connIndex, 1);
      setConexoes(novasConexoes);
      setSelecionadoDir(null);
      return;
    }

    if (selecionadoDir === id) {
      setSelecionadoDir(null);
    } else {
      setSelecionadoDir(id);
      if (selecionadoEsq !== null) {
        setConexoes([...conexoes, { esqId: selecionadoEsq, dirId: id }]);
        setSelecionadoEsq(null);
        setSelecionadoDir(null);
      }
    }
  };

  const getEsqClass = (id: number) => {
    const connIndex = conexoes.findIndex(c => c.esqId === id);
    if (connIndex !== -1) return coresPares[connIndex % coresPares.length];
    if (selecionadoEsq === id) return 'border-amber-400 bg-amber-400/20 shadow-[0_0_10px_rgba(251,191,36,0.3)] text-white';
    return 'bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700 cursor-pointer';
  };

  const getDirClass = (id: number) => {
    const connIndex = conexoes.findIndex(c => c.dirId === id);
    if (connIndex !== -1) return coresPares[connIndex % coresPares.length];
    if (selecionadoDir === id) return 'border-amber-400 bg-amber-400/20 shadow-[0_0_10px_rgba(251,191,36,0.3)] text-white';
    return 'bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700 cursor-pointer';
  };

  const rodadaCompleta = idsDaRodada.length > 0 && idsDaRodada.every(id => conexoes.some(c => c.esqId === id));
  const ultimaRodada = rodadaAtual >= rodadas.length - 1;
  const isValido = conexoes.length === pares.length && pares.length > 0;

  const handleValidar = () => {
    const tudoCerto = conexoes.every(conn => conn.esqId === conn.dirId) && isValido;
    onComplete(tudoCerto);
  };

  return (
    <div className="w-full flex flex-col justify-between gap-2 sm:gap-3 select-none">
      <p className="text-slate-300 text-fluid-body leading-tight text-center sm:text-left font-medium">
        {desafio.instrucao}
        {recompensa > 0 ? ` (acerto garante +${recompensa} Axé extra)` : ''}
        {fatiado && (
          <span className="ml-2 inline-block align-middle bg-sky-900/40 border border-sky-500/40 text-sky-300 px-2 py-0.5 rounded-full text-[10px] font-cyber font-bold uppercase tracking-wider">
            Rodada {rodadaAtual + 1}/{rodadas.length}
          </span>
        )}
      </p>

      {pares.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2.5 bg-slate-900/90 rounded-xl border border-slate-800">
            <p className="text-sky-400 text-[10px] sm:text-xs font-cyber font-bold mb-1.5 uppercase">Coluna A</p>
            <div className="space-y-1 sm:space-y-1.5">
              {termosEsq.map((item) => (
                <button
                  type="button"
                  key={item.idOriginal}
                  onClick={() => handleEsqClick(item.idOriginal)}
                  className={`w-full text-left p-1.5 sm:p-2 min-h-[44px] flex items-center rounded-lg border text-fluid-sm transition-all duration-200 leading-tight ${getEsqClass(item.idOriginal)}`}
                >
                  {item.termo}
                </button>
              ))}
            </div>
          </div>
          <div className="p-1.5 sm:p-2.5 bg-slate-900/90 rounded-xl border border-slate-800">
            <p className="text-sky-400 text-[10px] sm:text-xs font-cyber font-bold mb-1.5 uppercase">Coluna B</p>
            <div className="space-y-1 sm:space-y-1.5">
              {termosDir.map((item) => (
                <button
                  type="button"
                  key={item.idOriginal}
                  onClick={() => handleDirClick(item.idOriginal)}
                  className={`w-full text-left p-1.5 sm:p-2 min-h-[44px] flex items-center rounded-lg border text-fluid-sm transition-all duration-200 leading-tight ${getDirClass(item.idOriginal)}`}
                >
                  {item.termo}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-slate-500 text-xs italic text-center py-4">Nenhum par disponível no desafio.</p>
      )}

      {rodadaCompleta && !ultimaRodada ? (
        <button
          onClick={() => setRodadaAtual((r) => r + 1)}
          className="btn-cyber w-full py-2.5 sm:py-3 mt-1 rounded-xl shadow-lg bg-sky-600 text-white font-cyber font-black hover:bg-sky-500 transition-colors uppercase tracking-wider text-fluid-body border border-sky-400"
        >
          Próximos pares ({rodadaAtual + 2}/{rodadas.length})
        </button>
      ) : isValido && ultimaRodada ? (
        <button
          onClick={handleValidar}
          className="btn-cyber w-full py-2.5 sm:py-3 mt-1 rounded-xl shadow-lg bg-emerald-500 text-slate-950 font-cyber font-black hover:bg-emerald-400 transition-colors uppercase tracking-wider text-fluid-body border border-emerald-300"
        >
          Validar conexões {recompensa > 0 ? `(+${recompensa} Axé)` : ''}
        </button>
      ) : (
        <button disabled className="w-full py-2.5 sm:py-3 mt-1 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-500 font-cyber font-bold uppercase tracking-wider text-fluid-body cursor-not-allowed">
          Conecte todos os pares{fatiado ? ` da rodada ${rodadaAtual + 1}` : ''}
        </button>
      )}
    </div>
  );
}
